import { FC, PropsWithChildren } from "react";
import { Switch } from "shared/components/forms/Switch";
import {
  ApplicationFee,
  VerificationSettings
} from "types/landlord/verificationTemplate";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Card } from "shared/components/Card";
import { formatToCurrency } from "utils/currency";
import { verificationLogos } from "../../slices/VerificationsSlice/constants";
import { Show } from "shared/components";
import { ExecutionModes, VerificationNames } from "shared/types";
import { VerificationSchema } from "types/landlord/verificationTemplate";
import { useTemplateContext } from "../../provider";
import { CheckComponent } from "../CheckComponent";
import { cx } from "shared/utils";
import { ExecutionModeSelector } from "shared/app/VerificationTemplate/components/ExecutionModeSelector";

interface Props {
  title: string;
  description: string;
  onToggleChange?: (checked: boolean) => void;
  updateExecutionMode?: (
    executionMode: ExecutionModes,
    schemaName: VerificationNames
  ) => void;
  toggleEnabled?: boolean;
  onButtonClick?: () => void;
  sideImageURL: string;
  fee?: ApplicationFee;
  readonly?: boolean;
  verificationSchema: VerificationSchema;
  cardClassName?: string;
  imageClassName?: string;
  hideFooter?: boolean;
}

export const ServiceCard: FC<PropsWithChildren<Props>> = (props) => {
  const { readOnly, template, partnerType } = useTemplateContext();
  const {
    title,
    description,
    onToggleChange,
    updateExecutionMode,
    toggleEnabled,
    onButtonClick,
    sideImageURL,
    children,
    fee,
    verificationSchema,
    cardClassName,
    imageClassName,
    hideFooter
  } = props;

  return (
    <Card className="flex md:flex-row flex-col rounded-md">
      <img
        // TODO: fix footer -- then show icon back
        className={cx(
          "h-[244px] w-56 hidden md:block rounded-l-md",
          imageClassName
        )}
        src={sideImageURL}
        alt={title + " service image"}
      />
      <div
        className={cx(
          "p-8 rounded-md bg-background-1000 w-full",
          cardClassName
        )}
      >
        <div className="flex flex-col justify-between h-full">
          <div className="flex gap-8 items-center justify-between">
            <div className="space-y-2">
              <div className="text-base font-bold text-content-1000">
                {title}
              </div>
              <div className="text-sm text-content-400">{description}</div>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm font-bold text-primary-500">
                {fee?.cents ? formatToCurrency(fee) : "Free"}
              </p>
              <Show if={!readOnly}>
                {onToggleChange && toggleEnabled !== undefined ? (
                  <Switch checked={toggleEnabled} onChange={onToggleChange} />
                ) : null}
              </Show>
              <Show if={readOnly}>
                <CheckComponent checked={Boolean(toggleEnabled)} />
              </Show>
            </div>
          </div>

          <Show if={!hideFooter}>
            <div className="pt-6 flex items-center justify-between border-t border-alpha-40">
              <div className="flex items-center gap-4 h-10">
                <Show
                  if={
                    ![
                      VerificationNames.EvictionReport,
                      VerificationNames.CriminalReport
                    ].includes(verificationSchema.name)
                  }
                >
                  {verificationLogos[verificationSchema.name]?.map((logo) => (
                    <img
                      key={logo.src}
                      src={logo.src}
                      alt={logo.alt}
                      width={logo.width}
                      height={logo.height}
                    />
                  ))}
                </Show>

                <Show if={children}>{children}</Show>
              </div>

              <div className="flex gap-2">
                <Show
                  if={[
                    VerificationNames.EvictionReport,
                    VerificationNames.CriminalReport,
                    VerificationNames.CreditReport
                  ].includes(verificationSchema.name)}
                >
                  <ExecutionModeSelector
                    updateExecutionMode={updateExecutionMode}
                    serviceName={verificationSchema.name}
                    readOnly={readOnly}
                    executionMode={
                      (
                        template?.verification_schemas[partnerType][
                          verificationSchema.name
                        ] as VerificationSettings | undefined
                      )?.execution_mode
                    }
                  />
                </Show>

                <Show if={Boolean(onButtonClick)}>
                  <Button
                    variant={ButtonVariant.BASE}
                    size={ButtonSize.SM}
                    onClick={onButtonClick}
                  >
                    {readOnly ? "View configuration" : "Configure"}
                  </Button>
                </Show>
              </div>
            </div>
          </Show>
        </div>
      </div>
    </Card>
  );
};
